import { Duration } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';

import { Text } from '~components/Typography';
import type { TMedia } from '~constants/media';
import { useMediaContentContext } from '~context/media-content';
import type { Source } from '~context/media-content/types';
import {
	MediaMessageContainer,
	TextMessageWrapper,
} from '~entities/messages/ui/styles';
import { SextingPackFromPlaceReason } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { useUserId } from '~hooks/useUserId';
import { Loader } from '~shared/ui/atoms';
import { ImageRenderer } from '~shared/ui/molecules';

import LockIcon from '../../../../../public/assets/icons/lock.svg';
import Play from '../../../../../public/assets/icons/play.svg';
import type { TMessage } from '../../model/types';
import { useMediaPack } from '../../model/useMediaPack';
import { SextingPackActions } from './Actions';
import { Lock, Packs, SecureImage } from './styles';

type Props = {
	botId: string;
	message: TMessage;
	showPaid: boolean;
	onAction: () => void;
};

export const SextingPackMessage = ({
	botId,
	message,
	showPaid,
	onAction,
}: Props) => {
	const { text, meta } = message;
	const showText = meta?.mediaSelling ?? false;

	const userId = useUserId();
	const prevShowPaid = useRef(showPaid);

	const { open, openedId, update } = useMediaContentContext();

	const cost = useMemo(
		() => meta?.tariffication.cost || 0,
		[meta?.tariffication.cost]
	);

	const { data, isLoading, refetch } = useMediaPack({
		userId,
		botId,
		messageId: message.id,
	});

	const annalsSinglePhotoClicked = annals.sextingPack.usePhotoClicked({
		botClientId: botId,
		mediaId: message.meta?.MediaId,
	});
	const annalsViewAllClicked = annals.sextingPack.useViewAllClicked({
		botClientId: botId,
		mediaId: message.meta?.MediaId,
	});
	const annalsUnlockAllClicked = annals.sextingPack.useUnlockAllClicked({
		botClientId: botId,
		mediaId: message.meta?.MediaId,
	});
	const annalsGalleryNavigationClicked =
		annals.sextingPack.useNavigationClicked({
			botClientId: botId,
			mediaId: message.meta?.MediaId,
		});

	const handleUnlockFromChat = () => {
		onAction();
		void annalsUnlockAllClicked({
			reason: SextingPackFromPlaceReason.Chat,
		});
	};

	const handleUnlockFromGallery = () => {
		onAction();
		void annalsUnlockAllClicked({
			reason: SextingPackFromPlaceReason.Gallery,
		});
	};

	const handleNavigation: Source['commonNavAction'] = (direction) => {
		void annalsGalleryNavigationClicked({
			direction,
		});
	};

	const handleOpenPack = (index?: number) => {
		const refs: Source[] = data.map((item) => {
			return {
				url: item.reference,
				preview: item.preview,
				mediaLinks: item.mediaLinks,
				messageId: message.id,
				type: item.type,
				videoDuration: item.videoDuration,
				mediaId: item.mediaId,
				commonNavAction: handleNavigation,
				payload: {
					action: handleUnlockFromGallery,
					cost,
					secure: true,
					description: meta?.packName || '',
				},
			};
		});

		if (typeof index !== 'number') {
			open(refs, 0);
			void annalsViewAllClicked();
		} else {
			open(refs, index);
			void annalsSinglePhotoClicked({
				image_media_id: data[index].mediaId,
			});
		}
	};

	useEffect(() => {
		if (showPaid !== prevShowPaid.current) {
			void refetch();
			prevShowPaid.current = showPaid;
		}
	}, [refetch, showPaid]);

	useEffect(() => {
		if (!openedId || message.id !== openedId) {
			return;
		}

		const refs: Source[] = data.map((item) => {
			return {
				url: item.reference,
				preview: item.preview,
				mediaLinks: item.mediaLinks,
				messageId: message.id,
				commonNavAction: handleNavigation,
				type: item.type,
				videoDuration: item.videoDuration,
				mediaId: item.mediaId,
				payload: {
					action: handleUnlockFromGallery,
					cost,
					secure: true,
					description: meta?.packName || '',
				},
			};
		});

		update(refs);

		//eslint-disable-next-line
		}, [openedId, onAction, showPaid, data, cost, meta?.packName]);

	if (isLoading || !data.length) {
		return (
			// 500px prevents content jumps after load
			<div style={{ height: '550px' }}>
				<Loader />
			</div>
		);
	}

	const rows = () => {
		if (data.length >= 7) {
			return 3;
		} else if (data.length >= 4) {
			return 2;
		} else {
			return 1;
		}
	};

	return (
		<MediaMessageContainer>
			<Packs rows={rows()}>
				{data.slice(0, 9).map((item, index) => {
					const fallbackUrl = showPaid ? item.preview : item.reference;

					return (
						<SecureImage
							key={item.preview}
							onClick={() => handleOpenPack(index)}
						>
							<ImageRenderer
								quality={80}
								item={item.mediaLinks as unknown as TMedia[]}
								fallback={fallbackUrl}
								width={100}
								height={150}
								alt=""
								showPreview={showPaid}
								showLoader
							/>
							{item.type === 'pack-video' ? (
								<>
									<Play />
									<Lock>
										<Text size="M" color="semi-transparent">
											{Duration.fromObject({
												seconds: item.videoDuration,
											}).toFormat('mm:ss')}
										</Text>
									</Lock>
								</>
							) : null}
						</SecureImage>
					);
				})}
				<Lock>
					<LockIcon />
					<Text size="M" color="semi-transparent">
						{data.length}
					</Text>
				</Lock>
			</Packs>
			{data.length > 0 ? (
				<SextingPackActions
					itemsLength={data.length}
					showPaid={showPaid}
					cost={cost}
					onOpenPack={handleOpenPack}
					onUnlockPack={handleUnlockFromChat}
				/>
			) : null}

			{/*  TODO: use proper typography */}
			{showText && <TextMessageWrapper>{text}</TextMessageWrapper>}
		</MediaMessageContainer>
	);
};
